(() => {
    if (customElements.get('sticky-tabs-section')) return;

    class StickyTabsSection extends HTMLElement {
        constructor() {
            super();

            this.slider = null;
            this.inertiaFrame = null;
            this.inertiaEnabled = false;

            this.targetScrollY = 0;
            this.smoothScrollY = 0;
            this.lastAppliedOffset = 0;

            this.lastScrollY = 0;
            this.currentOffset = 0;
            this.targetOffset = 0;

            this.mediaQuery = window.matchMedia('(max-width: 989px)');
            this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

            this.onMediaChange = this.onMediaChange.bind(this);
            this.onPrevClick = this.onPrevClick.bind(this);
            this.onNextClick = this.onNextClick.bind(this);
            this.updateArrows = this.updateArrows.bind(this);

            this.onWindowScroll = this.onWindowScroll.bind(this);
            this.onWindowResize = this.onWindowResize.bind(this);
            this.inertiaTick = this.inertiaTick.bind(this);
        }

        connectedCallback() {
            this.sliderEl = this.querySelector('.sticky-tabs-section__mobile-slider');
            this.prevButton = this.querySelector('.sticky-tabs-section__arrow--prev');
            this.nextButton = this.querySelector('.sticky-tabs-section__arrow--next');
            this.desktopTrackInner = this.querySelector('.sticky-tabs-section__desktop-track-inner');

            this.prevButton?.addEventListener('click', this.onPrevClick);
            this.nextButton?.addEventListener('click', this.onNextClick);

            this.mediaQuery.addEventListener('change', this.onMediaChange);
            this.reducedMotionQuery.addEventListener('change', this.onMediaChange);

            this.onMediaChange();
        }

        disconnectedCallback() {
            this.prevButton?.removeEventListener('click', this.onPrevClick);
            this.nextButton?.removeEventListener('click', this.onNextClick);

            this.mediaQuery.removeEventListener('change', this.onMediaChange);
            this.reducedMotionQuery.removeEventListener('change', this.onMediaChange);

            this.destroySlider();
            this.destroyDesktopInertia();
        }

        onMediaChange() {
            if (this.mediaQuery.matches) {
                this.destroyDesktopInertia();
                this.initSlider();
            } else {
                this.destroySlider();
                this.initDesktopInertia();
            }
        }

        initSlider() {
            if (this.slider || !this.sliderEl || typeof Splide === 'undefined') return;

            this.slider = new Splide(this.sliderEl, {
                type: 'loop',
                perPage: 1,
                perMove: 1,
                gap: 24,
                arrows: false,
                pagination: true,
                drag: true,
                speed: 1200,
                autoplay: true,
                interval: 4000,
                pauseOnHover: false,
                pauseOnFocus: false,
            });

            this.slider.on('mounted move moved updated resize', this.updateArrows);
            this.slider.mount();
            this.updateArrows();
        }

        destroySlider() {
            if (!this.slider) return;

            this.slider.destroy(true);
            this.slider = null;
        }

        initDesktopInertia() {
            if (this.inertiaEnabled || !this.desktopTrackInner || this.reducedMotionQuery.matches) return;

            this.inertiaEnabled = true;

            this.targetScrollY = window.scrollY;
            this.smoothScrollY = window.scrollY;
            this.lastScrollY = window.scrollY;
            this.currentOffset = 0;
            this.targetOffset = 0;
            this.lastAppliedOffset = 0;

            this.desktopTrackInner.style.transform = 'translate3d(0, 0, 0)';

            window.addEventListener('scroll', this.onWindowScroll, { passive: true });
            window.addEventListener('resize', this.onWindowResize);

            this.inertiaFrame = window.requestAnimationFrame(this.inertiaTick);
        }

        destroyDesktopInertia() {
            this.inertiaEnabled = false;

            window.removeEventListener('scroll', this.onWindowScroll);
            window.removeEventListener('resize', this.onWindowResize);

            if (this.inertiaFrame) {
                window.cancelAnimationFrame(this.inertiaFrame);
                this.inertiaFrame = null;
            }

            if (this.desktopTrackInner) {
                this.desktopTrackInner.style.transform = '';
            }
            this.targetScrollY = 0;
            this.smoothScrollY = 0;
            this.lastScrollY = 0;
            this.currentOffset = 0;
            this.targetOffset = 0;
            this.lastAppliedOffset = 0;
        }

        onWindowScroll() {
            const currentScrollY = window.scrollY;
            const delta = currentScrollY - this.lastScrollY;

            this.targetScrollY = currentScrollY;
            this.lastScrollY = currentScrollY;

            if (Math.abs(delta) < 0.5) return;

            /**
             * delta > 0 — скроллим вниз
             * delta < 0 — скроллим вверх
             *
             * Инерцию даём в сторону скролла:
             * вниз = positive translateY
             * вверх = negative translateY
             */
            const strength = 0.46;
            const maxLag = 38;

            const nextOffset = Math.max(-maxLag, Math.min(maxLag, delta * strength));

            /**
             * Не перезаписываем offset слишком резко.
             * Добавляем импульс, чтобы инерция была заметна и вниз, и вверх.
             */
            this.targetOffset += nextOffset;

            this.targetOffset = Math.max(-maxLag, Math.min(maxLag, this.targetOffset));
        }

        onWindowResize() {
            this.targetScrollY = window.scrollY;
            this.smoothScrollY = window.scrollY;
            this.lastScrollY = window.scrollY;
            this.currentOffset = 0;
            this.targetOffset = 0;
            this.lastAppliedOffset = 0;

            if (this.desktopTrackInner) {
                this.desktopTrackInner.style.transform = 'translate3d(0, 0, 0)';
            }
        }

        inertiaTick() {
            if (!this.inertiaEnabled || !this.desktopTrackInner) return;

            const rect = this.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const isNearSection = rect.top < viewportHeight * 1.25 && rect.bottom > -viewportHeight * 0.25;

            if (isNearSection) {
                /**
                 * currentOffset догоняет targetOffset.
                 * targetOffset постепенно затухает к нулю.
                 *
                 * Это даёт ощущение инерции без GSAP и без pin-spacer.
                 */
                const followEase = 0.075;
                const decay = 0.91;

                this.currentOffset += (this.targetOffset - this.currentOffset) * followEase;
                this.targetOffset *= decay;

                if (Math.abs(this.currentOffset) < 0.05 && Math.abs(this.targetOffset) < 0.05) {
                    this.currentOffset = 0;
                    this.targetOffset = 0;
                }

                if (Math.abs(this.currentOffset - this.lastAppliedOffset) > 0.1) {
                    this.desktopTrackInner.style.transform = `translate3d(0, ${this.currentOffset}px, 0)`;
                    this.lastAppliedOffset = this.currentOffset;
                }
            } else {
                this.targetScrollY = window.scrollY;
                this.smoothScrollY = window.scrollY;
                this.lastScrollY = window.scrollY;
                this.currentOffset = 0;
                this.targetOffset = 0;

                if (Math.abs(this.lastAppliedOffset) > 0.1) {
                    this.desktopTrackInner.style.transform = 'translate3d(0, 0, 0)';
                    this.lastAppliedOffset = 0;
                }
            }

            this.inertiaFrame = window.requestAnimationFrame(this.inertiaTick);
        }

        onPrevClick() {
            if (!this.slider) return;
            this.slider.go('<');
        }

        onNextClick() {
            if (!this.slider) return;
            this.slider.go('>');
        }

        updateArrows() {
            if (!this.slider || !this.prevButton || !this.nextButton) return;

            const currentIndex = this.slider.index;
            const lastIndex = this.slider.Components.Controller.getEnd();

            this.prevButton.disabled = currentIndex <= 0;
            this.nextButton.disabled = currentIndex >= lastIndex;

            this.prevButton.classList.toggle('is-disabled', currentIndex <= 0);
            this.nextButton.classList.toggle('is-disabled', currentIndex >= lastIndex);
        }
    }

    customElements.define('sticky-tabs-section', StickyTabsSection);
})();